import { Star, Quote, TrendingUp, DollarSign, Users } from "lucide-react";
import { useState, useEffect } from "react";

const SocialProof = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('social-proof');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  const testimonials = [
    {
      quote: "I was skeptical about 'zero fees' until my first payout. $47,000—every single cent landed in my account. VCM is the real deal.",
      role: "Fitness Creator",
      earnings: "$380K+ in 2024",
      followers: "3.5M+ followers",
      rating: 5,
    },
    {
      quote: "Switching to VCM was the best decision I made. The support team actually responds, and my earnings went through the roof.",
      role: "Entertainment Creator",
      earnings: "$520K+ in 2024",
      followers: "8M+ followers",
      rating: 5,
    },
    {
      quote: "The community alone is worth it. I'm battling with creators who taught me more in 3 months than I learned in 2 years solo.",
      role: "Lifestyle Creator",
      earnings: "$210K+ in 2024",
      followers: "500K+ followers",
      rating: 5,
    },
  ];

  const stats = [
    { icon: DollarSign, value: "$10M+", label: "Paid to Creators", sublabel: "Zero fees taken" },
    { icon: TrendingUp, value: "300%", label: "Average Growth", sublabel: "First 90 days" },
    { icon: Users, value: "<5%", label: "Acceptance Rate", sublabel: "Elite only" },
  ];

  return (
    <section id="social-proof" className="py-24 md:py-32 bg-card relative overflow-hidden">
      {/* Enhanced luxury background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-gradient-to-br from-primary/10 via-secondary/5 to-transparent rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-gradient-to-tl from-accent/10 via-tertiary/5 to-transparent rounded-full blur-[150px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-secondary/5 via-transparent to-primary/5 rounded-full blur-[180px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-3">
            Real Results, Real Creators
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Hear From the <span className="gradient-text">Elite</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Our creators don't just earn more—they dominate their categories
          </p>
        </div>

        {/* Stats Row - Moved above testimonials */}
        <div 
          className={`grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-20 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            const gradients = [
              "from-primary/10 via-primary/5 to-secondary/10",
              "from-secondary/10 via-tertiary/5 to-tertiary/10",
              "from-tertiary/10 via-accent/5 to-accent/10"
            ];
            return (
              <div 
                key={index} 
                className={`relative p-8 rounded-2xl bg-gradient-to-br ${gradients[index]} border border-border/50 text-center group hover:border-primary/40 hover:shadow-[0_0_30px_rgba(217,70,239,0.15)] transition-all duration-300`}
              >
                <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 border border-primary/30 mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent mb-1 animate-gradient-shift bg-[length:200%_auto]">
                  {stat.value}
                </div>
                <div className="text-sm font-semibold text-foreground">{stat.label}</div>
                <div className="text-xs text-muted-foreground">{stat.sublabel}</div>
              </div>
            );
          })}
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {testimonials.map((testimonial, index) => {
            const gradients = [
              "from-primary/5 via-background to-secondary/5",
              "from-secondary/5 via-background to-tertiary/5",
              "from-tertiary/5 via-background to-accent/5"
            ];
            return (
              <div
                key={index}
                className={`group relative p-8 rounded-2xl bg-gradient-to-br ${gradients[index]} border border-border hover:border-primary/50 transition-all duration-500 hover:shadow-[0_0_50px_rgba(217,70,239,0.15)] ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${200 + 150 * index}ms` }}
              >
                {/* Quote icon with enhanced gradient */}
                <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-gradient-to-br from-primary via-secondary to-accent flex items-center justify-center shadow-lg shadow-primary/30 group-hover:scale-110 transition-transform duration-300">
                  <Quote className="w-5 h-5 text-white" />
                </div>

                {/* Earnings badge with gradient */}
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-accent/15 to-tertiary/15 border border-accent/40 mb-4">
                  <DollarSign className="w-3.5 h-3.5 text-accent" />
                  <span className="text-xs font-bold text-accent">{testimonial.earnings}</span>
                </div>

                {/* Stars */}
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-primary fill-primary" />
                  ))}
                </div>

                {/* Quote */}
                <p className="text-foreground/90 leading-relaxed mb-6 text-base">
                  "{testimonial.quote}"
                </p>

                {/* Attribution */}
                <div className="pt-4 border-t border-border/50">
                  <p className="font-semibold text-foreground">{testimonial.role}</p>
                  <p className="text-sm text-muted-foreground">{testimonial.followers}</p>
                </div>

                {/* Hover overlay */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
              </div>
            );
          })}
        </div>

        {/* FOMO Banner with enhanced gradient */}
        <div 
          className={`mt-16 max-w-3xl mx-auto transition-all duration-700 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="p-6 rounded-2xl bg-gradient-to-r from-primary/15 via-secondary/15 to-accent/15 border border-primary/30 text-center shadow-[0_0_40px_rgba(217,70,239,0.1)]">
            <p className="text-lg font-semibold bg-gradient-to-r from-foreground via-foreground to-foreground/80 bg-clip-text text-transparent">
              ⚡ Being VCM means more than streaming—it's access to the industry's most exclusive network and next-level growth.
            </p>
          </div>
        </div>

        {/* Bottom gradient line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-secondary/30 to-transparent" />
      </div>
    </section>
  );
};

export default SocialProof;
